import { useNavigate } from 'react-router-dom'
import { buildQuoteDetailsNavigation } from 'modules/finance/components/Quotes/utils'
import { Box } from '@factorialco/deprecated-design-system'
import { F0Dialog } from '@factorialco/f0-react'
import { Input } from '@factorialco/f0-react/dist/experimental'
import {
  CreateFinanceQuoteDocument,
  FinanceEnumsQuoteTypeEnum,
  QuotesListTableDocument,
} from 'generated/resources/graphql'

import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { QuoteTypeSelector } from 'modules/finance/components/selectors/QuoteTypeSelector'

interface CreateQuoteModalProps {
  onClose: () => void
  dealId?: string
  clientId?: string
  defaultName?: string
  isOpen: boolean
  canCreateProjectQuotes?: boolean
}

interface CreateQuoteModalFormValues {
  name?: string
  quoteType: FinanceEnumsQuoteTypeEnum
}

export const CreateQuoteModal = ({
  onClose,
  dealId,
  clientId,
  defaultName,
  isOpen,
  canCreateProjectQuotes = true,
}: CreateQuoteModalProps) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const [createQuote] = useMutation(CreateFinanceQuoteDocument, {
    extractorFn: (data) => data.financeMutations.createFinanceQuote,
    refetchQueries: [QuotesListTableDocument],
  })

  const defaultQuoteType: FinanceEnumsQuoteTypeEnum = canCreateProjectQuotes
    ? 'project'
    : 'products'

  const form = useForm<CreateQuoteModalFormValues>({
    defaultValues: {
      name: defaultName,
      quoteType: defaultQuoteType,
    },
    validateOnChange: false,
    validateOnBlur: false,
    validate: (data) => {
      const errors: FormErrors<CreateQuoteModalFormValues> = {}

      if (!data.name?.trim()) {
        errors.name = i18n.t('common.validations.empty')
      }

      return errors
    },
    onSubmit: async (values) => {
      if (!values.name?.trim()) {
        return {
          errors: [],
          success: false,
        }
      }

      const result = await createQuote({
        variables: {
          name: values.name,
          clientId: clientId,
          dealId: dealId,
          quoteType: values.quoteType,
        },
      })

      const handled = handleResult(result, (parsedErrors) => {
        if (parsedErrors.name || parsedErrors.quoteType) {
          form.setErrors({
            name: parsedErrors.name,
            quoteType: parsedErrors.quoteType,
          })
        } else {
          app.setFlash({
            type: 'wrong',
            message: parsedErrors.errors ?? i18n.t('errors.generic_error'),
          })
        }
      })

      if (handled.success) {
        track(trackingEvents.accounting.quotes.submitCreate)
        app.setFlash({
          type: 'correct',
          message: 'quote created successfully',
          time: 2000,
        })

        const newQuoteId = handled.data?.quote?.id ?? ''
        navigate(buildQuoteDetailsNavigation(navigation, newQuoteId, dealId, clientId))

        return { success: true, data: values }
      } else {
        return {
          errors: [],
          success: false,
        }
      }
    },
  })

  return (
    <F0Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={i18n.t('finance.quotes.actions.create')}
      primaryAction={{
        label: i18n.t('finance.quotes.actions.create'),
        onClick: () => form.handleSubmit(),
      }}
      secondaryAction={{
        label: i18n.t('finance.quotes.actions.cancel'),
        onClick: onClose,
      }}
    >
      <Box gap="s32" flexDirection="column" justifyContent="flexEnd" width="full">
        <Input
          required
          label={i18n.t('finance.quotes.form.basic_information.name')}
          type="text"
          size="md"
          value={form.values.name || ''}
          onChange={(value) => form.setValues({ ...form.values, name: value })}
          error={form.errors.name}
          placeholder={i18n.t('finance.quotes.form.basic_information.name')}
        />
        {canCreateProjectQuotes && (
          <QuoteTypeSelector
            label={i18n.t('finance.quotes.form.management.budget')}
            value={form.values.quoteType}
            onChange={(value) => {
              form.setValues({ ...form.values, quoteType: value })
            }}
            error={form.errors.quoteType}
          />
        )}
      </Box>
    </F0Dialog>
  )
}
